import type {
  ClipboardEventHandler,
  KeyboardEventHandler,
  RefObject,
} from "react";
import { useT } from "@/i18n";
import { canvasCommentsMessages } from "@/i18n/messages/canvas-comments";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import type { AgentAvatarStyleKey } from "@/lib/dicebear-styles";
import type {
  AgentOption,
  CommentMentionOption,
} from "@/hooks/use-comment-action";
import { surfaceRoleAttributes } from "@/components/ui/surface-contract";
import { CommentMentionMenu } from "./comment-mention-menu";

type CommentEditorFieldProps = {
  avatarStyleKey: AgentAvatarStyleKey;
  selectedAgent: AgentOption | null;
  onClearAgent: () => void;
  text: string;
  onTextChange: (value: string) => void;
  onKeyDown: KeyboardEventHandler<HTMLTextAreaElement>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  showMentions: boolean;
  mentionOptions: CommentMentionOption[];
  mentionIndex: number;
  onMentionSelect: (option: CommentMentionOption) => void;
  rows: number;
  inputTestId: string;
  removeAgentTestId: string;
  mentionMenuTestId: string;
  placeholder?: string;
  onPaste?: ClipboardEventHandler<HTMLTextAreaElement>;
  textareaClassName?: string;
  className?: string;
  /** `well`: the textarea is an input surface; `plain`: it has no surface of
   *  its own and sits on its host's (the selection composer). */
  appearance?: "well" | "plain";
};

export function CommentEditorField({
  avatarStyleKey,
  selectedAgent,
  onClearAgent,
  text,
  onTextChange,
  onKeyDown,
  textareaRef,
  showMentions,
  mentionOptions,
  mentionIndex,
  onMentionSelect,
  rows,
  inputTestId,
  removeAgentTestId,
  mentionMenuTestId,
  placeholder,
  onPaste,
  textareaClassName,
  className,
  appearance = "well",
}: CommentEditorFieldProps) {
  const t = useT(canvasCommentsMessages);
  return (
    <div className={cn("relative flex min-h-0 flex-1 flex-col", className)}>
      {selectedAgent ? (
        <div className="mb-1 flex flex-wrap items-center gap-1">
          <span
            className="inline-flex items-center gap-1 rounded-control-sm px-1.5 py-0.5 text-appkit-mini font-semibold"
            style={{
              backgroundColor: `${selectedAgent.color}20`,
              borderWidth: 1,
              borderColor: `${selectedAgent.color}50`,
              color: selectedAgent.color,
            }}
          >
            @{selectedAgent.name}
            <button
              type="button"
              onClick={onClearAgent}
              className="text-muted hover:text-secondary"
              data-testid={removeAgentTestId}
            >
              <Icons.X className="h-2 w-2" />
            </button>
          </span>
        </div>
      ) : null}
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        placeholder={
          selectedAgent
            ? t("agentCommentPlaceholder")
            : (placeholder ?? t("mentionPlaceholder"))
        }
        className={cn(
          "min-h-0 w-full flex-1 resize-none overflow-auto bg-transparent text-appkit-small text-secondary outline-none placeholder:text-muted",
          textareaClassName,
        )}
        rows={rows}
        data-testid={inputTestId}
        {...(appearance === "well"
          ? surfaceRoleAttributes("input", { nativeLevel: "child" })
          : {})}
      />
      {showMentions ? (
        <CommentMentionMenu
          options={mentionOptions}
          avatarStyleKey={avatarStyleKey}
          highlightedIndex={mentionIndex}
          onSelect={onMentionSelect}
          testId={mentionMenuTestId}
        />
      ) : null}
    </div>
  );
}
